import { currencyScale } from '@wit-support/ui-domain';
import { formatMinor } from './format.js';
const TEMPLATE_ISSUE_KINDS = new Set([
    'unknown-field',
    'obsolete',
    'advice',
    'invalid',
]);
/**
 * Род замечания шаблона. Новый auto-ads присылает `kind`; старый — нет, тогда
 * по коду: `unknown-field` и всё «устаревшее» — расхождение с реестром,
 * остальное с `severity: 'warning'` — совет, ошибка — устаревшее значение.
 */
export function templateIssueKind(i) {
    const k = i.kind;
    if (k != null && TEMPLATE_ISSUE_KINDS.has(k))
        return k;
    if (k != null && k !== '')
        return 'obsolete';
    if (i.code === 'unknown-field')
        return 'unknown-field';
    if (/obsolete|deprecated|removed|stale/.test(i.code))
        return 'obsolete';
    return i.severity !== 'error' ? 'advice' : 'obsolete';
}
/** Шаблон разошёлся с реестром (неизвестное или устаревшее поле) — не совет и не ошибка ввода. */
export function isTemplateDrift(i) {
    const k = templateIssueKind(i);
    return k === 'unknown-field' || k === 'obsolete';
}
/** Значение формы не прошло проверку auto-ads при сохранении — ошибка под полем. */
export function isTemplateInvalid(i) {
    return templateIssueKind(i) === 'invalid';
}
/** Замечания шаблона по родам: расхождения с реестром, ошибки значений и советы. */
export function splitTemplateIssues(issues) {
    const drift = [];
    const invalid = [];
    const advice = [];
    for (const i of issues) {
        if (isTemplateDrift(i))
            drift.push(i);
        else if (isTemplateInvalid(i))
            invalid.push(i);
        else
            advice.push(i);
    }
    return { drift, invalid, advice };
}
/**
 * Все проблемы плана preview — пачки и запусков. Новый auto-ads присылает их
 * уже сведёнными (`allIssues`, у каждой `launchIndexes`) — берём как есть;
 * старый — проблемы пачки + проблемы каждого запуска (с его индексом),
 * свести их — дело {@link groupIssues}.
 */
export function planIssues(preview) {
    if (preview.allIssues != null)
        return [...preview.allIssues];
    return [
        ...preview.issues,
        ...preview.launches.flatMap((l) => l.issues.map((i) => (i.launchIndex == null ? { ...i, launchIndex: l.index } : i))),
    ];
}
/**
 * Сводит одинаковые замечания разных запусков в одно (поле, код, текст,
 * креатив, важность совпадают). Новый auto-ads уже присылает их сведёнными
 * (`launchIndexes`) — такие индексы объединяются с прочими.
 */
export function groupIssues(issues) {
    const out = [];
    const byKey = new Map();
    for (const i of issues) {
        const key = JSON.stringify([i.severity, i.code, i.fieldKey, i.itemIndex ?? null, i.message]);
        const idx = [...(i.launchIndexes ?? []), ...(i.launchIndex != null ? [i.launchIndex] : [])];
        const hit = byKey.get(key);
        if (hit) {
            for (const n of idx)
                if (!hit.launchIndexes.includes(n))
                    hit.launchIndexes.push(n);
            continue;
        }
        const g = { issue: i, launchIndexes: [...new Set(idx)] };
        byKey.set(key, g);
        out.push(g);
    }
    for (const g of out)
        g.launchIndexes.sort((a, b) => a - b);
    return out;
}
/**
 * Сведения плана, которые строкой замечаний не показываются (v4 §11.3):
 * `ad-conversion-domain-absent` — домен конверсий необязателен, его отсутствие
 * не новость; `spend-total` — итог в день виден крупно в «В день всего».
 */
export const HIDDEN_PLAN_INFO_CODES = new Set([
    'ad-conversion-domain-absent',
    'spend-total',
]);
/** Замечания плана для показа: без скрытых сведений (только `info`; ошибки видны всегда). */
export function visiblePlanIssues(issues) {
    return issues.filter((i) => !(i.severity === 'info' && HIDDEN_PLAN_INFO_CODES.has(i.code)));
}
/** «$21,99» / «1 234,50 EUR» — сумма сервера в русской записи (auto-ads `moneyRu`). */
const SERVER_USD_RE = /\$(\d{1,3}(?:\s\d{3})*|\d+),(\d{2})(?!\d)/g;
const SERVER_CUR_RE = /(\d{1,3}(?:\s\d{3})*|\d+),(\d{2}) ([A-Z]{3})\b/g;
/** Голый id поста Meta «<страница>_<пост>» — в тексте человеку его не показываем. */
const POST_ID_RE = /\b\d{6,}_(\d{6,})\b/g;
const minorOf = (units, frac) => Number(units.replace(/\s/g, '')) * 100 + Number(frac);
/**
 * Текст замечания сервера для человека (E-2 P3): суммы — тем же `formatMinor`,
 * что везде на экране («$21.99», а не «$21,99» рядом с «$21.99»), голый id
 * поста — коротко «…000001», как подпись поста в плане.
 */
export function issueMessageText(message) {
    return message
        .replace(SERVER_USD_RE, (_m, units, frac) => formatMinor(minorOf(units, frac), 'USD'))
        .replace(SERVER_CUR_RE, (m, units, frac, cur) => currencyScale(cur) === 2 ? formatMinor(minorOf(units, frac), cur) : m)
        .replace(POST_ID_RE, (_m, post) => `…${post.slice(-6)}`);
}
//# sourceMappingURL=issues.js.map